import { Link } from "react-router-dom";
import { useT } from "@/i18n/I18nProvider";
import { LanguageToggle } from "./LanguageToggle";
import { COMPANY } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

const CAP_LINKS: RouteKey[] = [
  "services.trade",
  "services.sourcing",
  "services.supply",
  "services.consulting",
  "commodities.grains",
  "commodities.food",
  "commodities.petrochemical",
];

const COMPLIANCE_LINKS: RouteKey[] = [
  "compliance.corporate",
  "compliance.kyc",
  "compliance.antifraud",
  "resources.profile",
  "resources.trade-docs",
];

const LEGAL_LINKS: RouteKey[] = [
  "legal.privacy",
  "legal.cookies",
  "legal.terms",
  "legal.accessibility",
  "legal.notice",
];

export function Footer() {
  const { t, path, locale } = useT();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-ink text-bone" data-testid="site-footer">
      <div className="container-bnm py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Identity */}
          <div className="md:col-span-4 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <img
                src="/images/brand/bnm-logo.png"
                alt="BNM Group"
                className="h-12 w-auto"
                width={727}
                height={320}
              />
              <span className="font-mono text-[10px] uppercase tracking-eyebrow text-bone/60">S.r.l.</span>
            </div>
            <p className="text-bone/70 text-sm max-w-[32ch] leading-relaxed">{t.footer.identity}</p>
            <address className="not-italic text-bone/70 text-sm leading-relaxed" data-testid="footer-address">
              {COMPANY.legalName}
              <br />
              {COMPANY.address.street}
              <br />
              {COMPANY.address.city}
              <br />
              {COMPANY.address.country[locale]}
            </address>
            <dl className="flex flex-col gap-1.5 font-mono text-[11px] text-bone/60" data-testid="footer-registry">
              {(
                [
                  [locale === "it" ? "Tel." : "Phone", COMPANY.phone, COMPANY.phoneHref],
                  ["Email", COMPANY.email, `mailto:${COMPANY.email}`],
                  ["PEC", COMPANY.pec, `mailto:${COMPANY.pec}`],
                  [locale === "it" ? "Sito" : "Web", COMPANY.websiteLabel, COMPANY.website],
                  ["VAT", COMPANY.vat, null],
                  ["REA", COMPANY.rea, null],
                  ["EU EORI", COMPANY.eoriEU, null],
                  ["UK EORI", COMPANY.eoriUK, null],
                ] as Array<[string, string, string | null]>
              ).map(([label, value, href]) => (
                <div key={label} className="flex gap-3">
                  <dt className="uppercase tracking-eyebrow text-bone/40 w-20 shrink-0">{label}</dt>
                  <dd>
                    {href ? (
                      <a
                        href={href}
                        className="hover:text-clay-400 transition-colors"
                        {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      >
                        {value}
                      </a>
                    ) : (
                      value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="pt-4">
              <span className="eyebrow !text-bone/60 block mb-3">{t.footer.languageLabel}</span>
              <div className="text-bone">
                <LanguageToggle testId="lang-toggle-footer" />
              </div>
            </div>
          </div>

          {/* Capabilities */}
          <div className="md:col-span-3">
            <h4 className="eyebrow !text-bone/60 mb-5">{t.footer.columns.capabilities}</h4>
            <ul className="flex flex-col gap-2">
              {CAP_LINKS.map((k, i) => (
                <li key={k}>
                  <Link
                    to={path(k)}
                    className="text-bone/90 hover:text-clay-400 transition-colors text-sm flex items-baseline gap-3"
                    data-testid={`footer-${k.replace(".", "-")}`}
                  >
                    <span className="font-mono text-[10px] text-bone/40">{String(i + 1).padStart(2, "0")}</span>
                    <span>{t.routes[k].label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Compliance & Resources */}
          <div className="md:col-span-3">
            <h4 className="eyebrow !text-bone/60 mb-5">{t.footer.columns.compliance}</h4>
            <ul className="flex flex-col gap-2">
              {COMPLIANCE_LINKS.map((k, i) => (
                <li key={k}>
                  <Link
                    to={path(k)}
                    className="text-bone/90 hover:text-clay-400 transition-colors text-sm flex items-baseline gap-3"
                    data-testid={`footer-${k.replace(".", "-")}`}
                  >
                    <span className="font-mono text-[10px] text-bone/40">{String(i + 1).padStart(2, "0")}</span>
                    <span>{t.routes[k].label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-2">
            <h4 className="eyebrow !text-bone/60 mb-5">{t.footer.columns.about}</h4>
            <ul className="flex flex-col gap-2 text-sm">
              <li><Link to={path("home")} className="text-bone/90 hover:text-clay-400 transition-colors" data-testid="footer-home">{t.nav.home}</Link></li>
              <li><Link to={path("about")} className="text-bone/90 hover:text-clay-400 transition-colors" data-testid="footer-about">{t.nav.about}</Link></li>
              <li><Link to={path("contact")} className="text-bone/90 hover:text-clay-400 transition-colors" data-testid="footer-contact">{t.nav.contact}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-bone/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p className="font-mono text-[11px] uppercase tracking-eyebrow text-bone/50">
            © {year} {COMPANY.legalName} · P. IVA {COMPANY.vat} · {t.footer.rights}
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {LEGAL_LINKS.map((k) => (
              <li key={k}>
                <Link
                  to={path(k)}
                  className="font-mono text-[11px] uppercase tracking-eyebrow text-bone/60 hover:text-clay-400 transition-colors"
                  data-testid={`footer-${k.replace(".", "-")}`}
                >
                  {t.routes[k].label}
                </Link>
              </li>
            ))}
          </ul>
          <span className="font-mono text-[11px] uppercase tracking-eyebrow text-bone/40">
            {locale === "it" ? "Italia" : "Italy"} · {locale.toUpperCase()}
          </span>
        </div>
      </div>
    </footer>
  );
}
